/**
 * Delivering a giftwrap to a person's inbox: their NIP-17 DM relays
 * (kind 10050) first, their NIP-65 read relays next, and the community relays
 * as a broad fallback.
 */

import type { NostrEvent } from "@nostrify/nostrify";
import { COMMUNITY_RELAYS } from "./config";

export interface InboxNostr {
  query(
    filters: Array<{ kinds: number[]; authors?: string[]; limit?: number }>,
    opts?: { signal?: AbortSignal },
  ): Promise<NostrEvent[]>;
  group(urls: string[]): {
    event(event: NostrEvent, opts?: { signal?: AbortSignal }): Promise<unknown>;
  };
  event(event: NostrEvent, opts?: { signal?: AbortSignal }): Promise<unknown>;
}

function wssOnly(urls: string[]): string[] {
  return urls.filter((u) => typeof u === "string" && u.startsWith("wss://"));
}

export async function publishToInbox(
  nostr: InboxNostr,
  recipientPub: string,
  event: NostrEvent,
): Promise<void> {
  const targets = new Set<string>(COMMUNITY_RELAYS);
  try {
    const [dm] = await nostr.query(
      [{ kinds: [10050], authors: [recipientPub], limit: 1 }],
      { signal: AbortSignal.timeout(4000) },
    );
    for (const t of dm?.tags ?? []) {
      if (t[0] === "relay" && t[1]) targets.add(t[1]);
    }
    if (!dm) {
      const [nip65] = await nostr.query(
        [{ kinds: [10002], authors: [recipientPub], limit: 1 }],
        { signal: AbortSignal.timeout(4000) },
      );
      for (const t of nip65?.tags ?? []) {
        // Read relays are where their inbox lives.
        if (t[0] === "r" && t[1] && t[2] !== "write") targets.add(t[1]);
      }
    }
  } catch {
    // fall through to the community-relay fallback
  }
  await nostr.group(wssOnly([...targets])).event(event, {
    signal: AbortSignal.timeout(8000),
  });
}
